import { memo, useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstantSearch } from 'react-instantsearch-hooks-web';

import { PointOfInterestCountryList } from '../PointOfInterestCountryList';
import { PinMap } from '../PinMap';
import { PoiSearch } from '../PoiSearch';
import { PoiSearchResults as SearchResults } from '../PoiSearch/PoiSearchResults';
import { PoiResultItem } from '../PoiSearch/PoiResultItem';
import { useUpdateNavigationBar } from '../NavigationBar/NavigationBarProvider';
import type { PointOfInterestRecord } from '../../map-types';
import { useDrawerState } from '../../providers/DrawerStateProvider';
import {
  MapContainer,
  MapContent,
  DrawerPanel,
  SearchWrapper,
  ListContent,
  DrawerToggle,
} from '../ExperienceLayout';

import { usePoiSelectedLocations } from './PoiSelectedLocationsProvider';

type Props = {
  visible?: boolean;
};

const PoiSearchResults: FC = () => {
  const {
    setSelectedProgramId,
    setSelectedCountrySubDivisionName,
    setSelectedCountryName,
    setShowCountryCard,
    setShowCityCard,
    setShowProgramCard,
  } = usePoiSelectedLocations();
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const { resetNavigationBar } = useUpdateNavigationBar();

  const handleResultClick = useCallback(
    (record: PointOfInterestRecord) => {
      setIndexUiState({
        ...indexUiState,
        query: undefined,
      });
      setSelectedProgramId(record.id);
      setSelectedCountryName(record.country);
      setSelectedCountrySubDivisionName({
        city: record.city,
        region: record.region,
      });
      setShowCountryCard(false);
      setShowCityCard(false);
      setShowProgramCard(true);
      resetNavigationBar();
    },
    [
      indexUiState,
      resetNavigationBar,
      setIndexUiState,
      setSelectedCountryName,
      setSelectedCountrySubDivisionName,
      setSelectedProgramId,
      setShowCityCard,
      setShowCountryCard,
      setShowProgramCard,
    ],
  );

  return (
    <SearchResults
      resultItemComponent={props => (
        <PoiResultItem {...props} onResultClick={handleResultClick} />
      )}
    />
  );
};

export const PointOfInterestExperience = memo(({ visible = true }: Props) => {
  const { t } = useTranslation();
  const { poiDrawerOpen: isOpen, togglePoiDrawer: toggle } = useDrawerState();

  return (
    <MapContainer $isVisible={visible}>
      <MapContent>
        {visible && (
          <DrawerPanel $isOpen={isOpen}>
            <SearchWrapper data-tour="search-wrapper" $isOpen={isOpen}>
              <PoiSearch
                placeholder={t('poiSearch.placeholder', {
                  defaultValue: 'Search the entire data set',
                })}
              >
                <PoiSearchResults />
              </PoiSearch>
            </SearchWrapper>
            <ListContent $isOpen={isOpen}>
              <PointOfInterestCountryList />
            </ListContent>
          </DrawerPanel>
        )}
        {visible && (
          <DrawerToggle $isOpen={isOpen} onClick={toggle}>
            {isOpen ? '‹' : '›'}
          </DrawerToggle>
        )}
        <PinMap visible={visible} drawerOpen={isOpen} />
      </MapContent>
    </MapContainer>
  );
});

PointOfInterestExperience.displayName = 'PointOfInterestExperience';
